<template>
  <v-container fluid justify-start :class="containerClass">

    <loading
      slot="activator"
      :active.sync="isDeleting"
      :can-cancel="false"
      loader="dots"
      :color="appColor_Progress"
    />

    <!-- Data Table -->
    <v-data-table
      v-model="selected"
      :headers="headers"
      :items="filteredFiles"
      :search="search"
      :custom-filter="customFilter"
      :loading="isLoading"
      :page.sync="page"
      show-select
      item-key="mediaFileId"
      :loading-text="getExpression('Alert', 'LoadingItems')"
      :footer-props="{ itemsPerPageOptions: itemsPerPageOptions }"
      class="elevation-2"
      >
      <v-progress-linear slot="progress" :color="appColor_Progress" indeterminate></v-progress-linear>

      <template v-slot:top>

        <!-- Header Toolbar -->
        <v-toolbar v-if="!isProfilePicker" dense dark flat :color="toolbarColor" class="mt-2">
          
          <app-back-button v-bind:color="toolbarColorDark" />

          <v-toolbar-title :class="titleClass">{{ pageTitle }}</v-toolbar-title>
          
          <v-spacer />
          
          <v-toolbar-items>
            <!-- Delete Button -->
            <v-btn
              multiple
              :class="`${appColor_DeleteButton} elevation-0`"
              :color="toolbarColorDark"
              :disabled="disableDelete"
              @click="remove"
              >
              <v-icon>delete</v-icon>
            </v-btn>

            <!-- Upload Button -->
            <v-btn
              @click="uploadMedia()"
              :color="toolbarColorDark"
              class="ml-3 elevation-0"
              >
              <v-icon large>cloud_upload</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>

        <!-- Picker-Only Toolbar -->
        <v-toolbar v-else dense dark flat :color="toolbarColor">
          <v-btn fab small @click="cancelPicker" class="mr-3 elevation-0" :color="toolbarColorDark">
            <v-icon>close</v-icon>
          </v-btn>
          
          <v-toolbar-title><span class="title">{{ pageTitle }}</span></v-toolbar-title>

          <v-spacer />

          <v-toolbar-items>
            <v-btn
              @click="selectMedia"
              large
              :color="toolbarColorDark"
              class="elevation-0"
              :disabled="!isMediaSelected"
              >
              <span class="subheading font-weight-medium">OK</span>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>

        <!-- Filters -->
        <v-row no-gutters class="mx-3 mt-2">

          <!-- Playlist Dropdown -->
          <template v-if="!isProfilePicker">
            <v-col cols="12" md="3">
              <v-select
                v-model="mediaPathTypeId"
                :items="mediaPathTypes"
                item-value="id"
                item-text="description"
                :label="getExpression('Label', 'Playlist')"
                :placeholder="getExpression('Label', 'SelectPlaylist')"
                @change="mediaPathTypeChanged"
                :disabled="isLoading"
                >
              </v-select>
            </v-col>
          </template>

          <!-- Search Text -->
          <v-col cols="12" :md="searchColWidth">
            <v-text-field
              v-model="search"
              append-icon="search"
              :label="getExpression('Label', 'Search')"
              :disabled="isLoading"
              clearable
              class="ml-3"
              >
            </v-text-field>
          </v-col>
        </v-row>
      </template>

      <template v-slot:item.thumbnail="{ item }">
        <router-link v-if="!isProfilePicker" :to="{name: currentRoute}" style="text-decoration: none">
          <span @click="preview(item)" class="text-center">
            <v-img v-if="getMediaType(item.contentType) !== 'audio'" width="50" height="50" :src="getThumbnailUrl(item)" alt=""></v-img>
            <v-icon v-else large color="blue darken-2">volume_up</v-icon>
          </span>
        </router-link>
        <template v-else>
          <v-img
            v-if="getMediaType(item.contentType) !== 'audio'" 
            width="50"
            height="50"
            :src="getThumbnailUrl(item)"
            class="text-center"
          ></v-img>
          <v-icon v-else large color="grey darken-2">volume_up</v-icon>
        </template>
      </template>

      <template v-slot:item.title="{ item }">
        <router-link v-if="!isProfilePicker" :to="{ name: currentRoute }">
          <span @click="edit(item)" class="text-center">{{ item.title }}</span>
        </router-link>
        <span v-else>{{ item.title }}</span>
      </template>

      <template v-slot:item.size="{ item }">
        <span class="text-left">{{ getFormattedSize(item.size, 1) }}</span>
      </template>

      <template v-slot:item.duration="{ item }">
        <span class="text-left">{{ getFormattedDuration(item.duration) }}</span>
      </template>

      <template v-slot:item.createDate="{ item }">
        <span class="text-left">
          <app-date :date="item.createDate" :format="'MMM-DD-YYYY'"/>
        </span>
      </template>
    </v-data-table>

    <!-- Media Previewer -->
    <v-dialog v-model="previewDialog" disabled max-width="800px" persistent>
      <media-previewer
        v-bind:type="previewType"
        v-bind:url="previewUrl"
        v-bind:title="previewTitle"
        v-on:close="closePreview"
        >
      </media-previewer>
    </v-dialog>

  </v-container>
</template>

<script>
import appService from '@/app.service'
import MediaPreviewer from '@/components/MediaPreviewer'
import { mapGetters } from 'vuex'
import { AppColor, Container, ThumbnailUrl, Context, Base64Image, AbbyMediaSourceId, UserRole, ImageUri, MediaPathTypeId } from '@/config.js'
import AppDate from '@/components/App/AppDate'
import AppBackButton from '@/components/App/AppBackButton'

export default {
  components: {
    MediaPreviewer,
    AppDate,
    AppBackButton
  },
  name: 'AbbyMediaContainer',
  props: {
    isPickerOnly: {
      type: Boolean,
      default: false
    },
    sourceId: {
      type: Number,
      default: -1
    },
    profileId: {
      type: Number,
      default: null
    },
    defaultMediaPathTypeId: {
      type: Number,
      default: -1
    },
    reloadMedia: {
      type: Boolean,
      default: false
    },
    defaultContainer: {
      type: String,
      default: null
    },
    toolbarColor: {
      type: String,
      default: AppColor.Light
    },
    toolbarColorDark: {
      type: String,
      default: AppColor.Medium
    }
  },
  data() {
    return {
      // colors
      appColor_DeleteButton: AppColor.DeleteButton,
      appColor_Progress: AppColor.Progress,

      // containers
      sharedLibrary: Container.AbbySharedLibrary,
      sharedLibraryId: Container.AbbySharedLibraryId,
      // uri
      thumbnailUrl_Abby: ThumbnailUrl.Abby,
      imageUri_AudioVideoPlaceholder: ImageUri.AudioVideoPlaceholder,

      // icons
      base64Image_AudioIcon: Base64Image.AudioIcon,

      // context
      context_Abby: Context.Abby,

      // roles
      role_Manager: UserRole.Manager,

      // source ids
      sourceId_Resident: AbbyMediaSourceId.Resident,

      // isPickerOnly is a property passed in
      isProfilePicker: this.isPickerOnly !== undefined ? this.isPickerOnly : false,
      
      page: 1,
      itemsPerPageOptions: [10, 15, 25, 100],
      containerId: null,
      containers: [],
      blobProperties: [],
      selected: [],
      files: [],
      mediaFiles: [],
      mediaPathTypes: [],
      mediaPathTypeId: null,
      isLoading: true,
      isSaving: false,
      isDeleting: false,
      search: '',

      previewDialog: false,
      previewType: null,
      previewTitle: null,
      previewUrl: null,

      editedIndex: -1,
      editedMediaFile: {
        filename: null,
        title: null,
        contentType: null,
        size: null,
        duration: null,
        previewUrl: null
      }
    }
  },
  computed: {
    ...mapGetters([
      'profile',
      'isAdmin',
      'isKioskManager',
      'currentRoute']),
    pageTitle () {
      if (this.isProfilePicker) {
        return `Select ${!this.isNullOrUndefined(this.mediaPathType)
        ? this.mediaPathType.shortDescription : 'Media'}`
      } else {  
        return 'Abby Shared Library'
      }
    },
    noDataText () {
      if (this.isLoading) return this.getExpression('Alert', 'OneMoment')

      if (this.containers.length === 0) {
        return this.getExpression('Alert', 'NoKiosksOrResidents')
      }
      return this.mediaFiles.length === 0
        ? this.getExpression('Alert', 'NoMedia')
        : ''
    },
    filteredFiles () {
      if (this.mediaFiles === null) return []

      const mediaPathTypeId = this.mediaPathTypeId
      return this.mediaFiles.filter(mf => {
        return (mf.mediaPathTypeId === mediaPathTypeId)
      })
    },
    isMediaSelected () {
      return this.selected.length > 0
    },
    headers () {
      var headers = []
      headers.push({
        value: 'thumbnail',
        align: 'center',
        width: '5%',
        sortable: false,
        class: "pr-0"
      })

      headers.push({
        text: 'Title',
        value: 'title',
        align: 'left',
        width: !this.isProfilePicker ? '35%' : '60%',
        class: "pr-0"
      })

      headers.push({
        text: 'Type',
        value: 'contentType',
        align: 'left',
        width: '15%',
        class: "pr-0"
      })

      if (!this.isProfilePicker) {
        headers.push({
          text: 'Duration',
          value: 'duration',
          align: 'left',
          width: '10%',
          class: "pr-0"
        })
      }

      headers.push({
        text: 'Size',
        value: 'size',
        align: 'left',
        width: '15%',
        class: "pr-0"
      })

      if (!this.isProfilePicker) {
        headers.push({
          text: 'Added',
          value: 'createDate',
          align: 'left',
          width: '15%',
          class: "pr-0"
        })
      }
      return headers
    },
    container () {
      if (this.containers.length === 0) return
      const containerId = this.containerId
      const containers = this.containers.filter(c => {
        return c.id === containerId
      })
      return containers.length > 0
        ? containers[0]
        : null
    },
    mediaPathType () {
      if (this.mediaPathTypes.length === 0) return null
      const mediaPathTypeId = this.mediaPathTypeId
      const pathTypes = this.mediaPathTypes.filter(pt => {
        return pt.id === mediaPathTypeId
      })
      return pathTypes.length > 0
        ? pathTypes[0]
        : null
    },

    // rules
    disableDelete () {
      return !this.isMediaSelected
    },

    // classes
    containerClass () {
      return {
        'pt-0': true,
        'pa-0': this.isProfilePicker
      }
    },
    titleClass () {
      return this.isSmallDevice
        ? 'body-2'
        : ''
    },
    searchColWidth () {
      return this.isProfilePicker
        ? '12'
        : '9'
    }
  },
  methods: {
    loadData () {
      if (!this.isValidSession) return
      const self = this
      self.isLoading = true

      // containers
      self.containers = [{
        id: self.sharedLibraryId,
        name: self.sharedLibrary 
      //name: 'shared-library-stage'
      }]
      self.containerId = self.sharedLibraryId

      // media path types
      const isSystem = self.isAdmin || self.isKioskManager
      const isSharable = true
      appService.getAbbyMediaPathTypes(isSystem, isSharable, self.profile.languageId).then(data => {

        // don't show radio shows (now obsolete)
        self.mediaPathTypes = data.mediaPathTypes
          .filter(pt => pt.id !== MediaPathTypeId.AudioRadioShows)

        // if used as a playlist media picker, use the default path Id (from property)
        self.mediaPathTypeId = self.isProfilePicker
          ? self.defaultMediaPathTypeId
          : self.mediaPathTypeId = data.mediaPathTypes[0].id

          // media files
          if (self.containerId !== null) {
            self.loadMediaFiles()
          } else {
            self.isLoading = false
          }
        }).catch(e => { self.handleCriticalError(e) })
    },

    loadMediaFiles () {
      if (this.containerId === null) {
        this.isLoading = false
        return
      }

      const self = this
      self.isLoading = true

      // if for the picker then set profileId and pathTypeId
      var profileId = null
      var mediaPathTypeId = null
      const isResident = self.sourceId === self.sourceId_Resident

      // setting these params tells the api to load only media available to this profile / mediaPath
      if (self.isProfilePicker) {
        profileId = self.profileId
        mediaPathTypeId = self.mediaPathTypeId
      }

      appService.getAbbyMediaByContainer(self.containerId, profileId, mediaPathTypeId, isResident).then(data => {
        if (data.errorSeverity > 0) {
          self.handleError(data)
        } else {
          self.mediaFiles = data.mediaFiles
          self.isLoading = false
        }
      }).catch(e => { self.handleCriticalError(e) })
    },

    remove () {
      const self = this

      self.$media.remove({
        context: self.context_Abby,
        deleteIds: self.selected.map(item => item.mediaFileId),
        onConfirm: () => { 
          self.isDeleting = true
        },
        onError: (data) => {
          self.isDeleting = false
          self.selected = []
          self.handleError(data)
        },
        onRemove: (deletedIds) => {
          deletedIds.forEach(mediaFileId => {
            const deletedMediaItems = self.filteredFiles.filter(f => {
              return f.mediaFileId === mediaFileId
            })
            if (deletedMediaItems.length > 0) {
              const indexDeleted = self.mediaFiles.indexOf(deletedMediaItems[0])
              self.mediaFiles.splice(indexDeleted, 1)
            }
          })
          self.isDeleting = false
          self.selected = []
        }
      })
    },

    edit (item) {
      const self = this

      self.editedIndex = this.mediaFiles.findIndex(mf => mf.mediaFileId === item.mediaFileId)
      self.editedMediaFile = Object.assign({}, item)

      const contentType = self.mediaPathType.category.toLowerCase()
      self.editedMediaFile.previewUrl = contentType !== 'audio'
        ? item.thumbnail
        : self.imageUri_AudioVideoPlaceholder

      // depending on which view is using this component, the container could be passed in differently
      const containerName = !self.isNullOrUndefined(self.container.name)
        ? self.container.name
        : self.container.container

      self.$media.edit({
        context: self.context_Abby,
        blob: self.editedMediaFile,
        container: containerName,
        toolbarColor: self.toolbarColor,
        toolbarColorDark: self.toolbarColorDark,

        onSubmit: (mediaFile) => {
          self.mediaFiles[self.editedIndex].title = mediaFile.title
        }
      })
    },

    uploadMedia () {
      const self = this

      const containerName = !self.isNullOrUndefined(self.container.name)
        ? self.container.name
        : self.container.container

      const contentType = self.mediaPathType.category.toLowerCase()

      self.$media.add({
        context: self.context_Abby,
        mediaPathTypeId: self.mediaPathTypeId,
        contentType: contentType,
        allowedTypes: self.mediaPathType.allowedTypes,
        container: containerName,
        toolbarColor: self.toolbarColor,
        toolbarColorDark: self.toolbarColorDark,

        onSubmit: (uploadResults) => {
          if (uploadResults.length > 0) {
            uploadResults.forEach(result => {
              if (result.success) {
                self.mediaFiles.unshift(result.mediaFile)
              }
            })
          }
        }
      })
    },

    getThumbnailUrl (item) {
      if (item.category !== 'Audio') {
        const filename = item.filename
        const ext = filename.split('.').pop()
        const thumnailName = filename.replace(ext, 'jpg')

        const url = `${this.thumbnailUrl_Abby}/${item.container}/${this.mediaPathType.path}/${thumnailName}`.replace('#', '%23')
        item.thumbnail = url

        return url
      } else {
        const url = this.base64Image_AudioIcon
        item.thumbnail = url

        return url
      }
    },

    preview (item) {
      const self = this
      const blobName = `${self.mediaPathType.path}/${item.filename}`
      const containerName = !self.isNullOrUndefined(self.container.name)
        ? self.container.name
        : self.container.container
        
      appService.getAbbyBlobUrl(containerName, blobName).then(data => {
        const previewType = self.mediaPathType.category.toLowerCase()
        self.previewUrl = data
        self.previewType = previewType
        self.previewTitle = item.title
        self.previewDialog = true
      }).catch(e => { self.handleCriticalError(e) })
    },

    closePreview () {
      this.previewType = null
      this.previewUrl = null
      this.previewDialog = false
    },

    getBlobUri (uri) {
      return this.replaceAll(uri, '%20', ' ')
    },

    replaceAll (inputString, searchString, replaceString) {
      return inputString.split(searchString).join(replaceString)
    },

    selectMedia () {
      this.$emit('ok', {
        selectedMedia: this.selected
      })
      this.selected = []
      this.mediaFiles = []
      this.mediaPathTypeId = null
    },

    cancelPicker () {
      this.selected = []
      this.mediaFiles = []
      this.mediaPathTypeId = null
      this.$emit('cancel')
    },

    customFilter (value, search, item) {
      return value != null &&
        search != null &&
        item.title.toString().toLowerCase().indexOf(search.toLowerCase()) !== -1
    },

    // events 
    mediaPathTypeChanged () {
      this.page = 1
    },

    handleError (result) {
      this.$error.show({
        text: result.errorMessage,
        severity: result.errorSeverity
      })
    }
  },
  created() {
    this.loadData()
  },
  watch: {
    reloadMedia (val) {
      if (val === null) return
      if (!this.isProfilePicker) return

      // when used as a media picker
      if (val) {
        this.mediaFiles = []
        this.loadData()
      }
    },

    isValidSession (val) {
      if (val === null) return
      this.loadData()
    }
  }
}
</script>
